import { IconButton,Stack,Tooltip,Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useContext,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillCopy,AiFillDelete,AiFillTool } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import { UnidadeType } from '../../service/Places';
import queryClient from '../../service/query';
import Unity from '../../service/Unity';
import { AuthBodyType } from '../../types';
import EditModal from './Modal/EditModal';
import QrCodeModal from './Modal/QrCodeModal';
import RemoveModal from './Modal/RemoveModal';

const UnityService = new Unity();

export default function UnityComponent({
  unity,
  set,
}
  : {
    unity: UnidadeType,
    selected: string[],
    set: string,
  }) {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const { hierarchyLabel } = useContext(UserContext);
  const { t } = useTranslation('translation');

  const { mutate, isPending } = useMutation({
    mutationKey: ['DELETE_UNITY'],
    mutationFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');
      const promise = UnityService.deleteUnity(token as AuthBodyType, unity.id, set)
        .then(() => true);
      return toast.promise(
        promise,
        {
          pending: t('notifications.deleteNotifWait'),
          success: `${hierarchyLabel.unidade} ${t('deleted')}!`,
        }, { toastId: 'deletando' });
    },
    onSuccess: () => {
      setOpenRemove(false);
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
    }
  });

  const { mutate: edit, isPending: editandoUnidade } = useMutation({
    mutationKey: ['EDIT_SET'],
    mutationFn: async (name: string) => {
      const response = toast.promise(async () => {
        trackEventMatomo('tela locais', 'click', 'button', 'edita unidade');
        const token = await AttToken();
        if (token) {
          const response = await UnityService.editUnity(token, unity.id, name);
          return response;
        }
      }, {
        success: `${hierarchyLabel.unidade} ${t('edited')}!`,
        error: t('places.erroSalvar'),
        pending: t('places.salvando')
      }, { toastId: 'editandoConjunto' });
      return response;
    },
    onSuccess: () => {
      setOpenEditModal(false);
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
    }
  });

  const { mutate: clone, isPending: clonandoUnidade } = useMutation({
    mutationKey: ['CLONE_UNITY'],
    mutationFn: async (unityid: string) => {
      const promise = toast.promise(async () => {
        trackEventMatomo('tela de locais', 'click', 'button', 'duplica unidade');
        const token = await AttToken();
        if (token) {
          const response = await UnityService.duplicateUnity(token, unityid);
          if (!response) throw new Error('erro clonar');
          return response;
        }
      }, {
        pending: t('places.Copiando_aguarde'),
      });
      return promise;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
    }
  });

  const onLoading = isPending || editandoUnidade || clonandoUnidade;

  const nameOf = 'unidade';
  const nameRemove = 'unidade';

  return (
    <>
      {openRemove && <RemoveModal
        mutate={mutate}
        openRemove={openRemove}
        setOpenRemove={setOpenRemove}
        nameRemove={nameRemove}
      />}
      {openEditModal && <EditModal
        isDisabled={onLoading}
        edit={edit}
        open={openEditModal}
        setOpen={setOpenEditModal}
        componentName={unity.nome}
        nameOf={nameOf}
      />}
      <Stack direction='row' width='100%' justifyContent='space-between' alignItems='flex-start' padding={2}>
        <Typography color='success' variant='h6' fontWeight={600}>
          {unity.nome}
        </Typography>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
        >
          <Tooltip title={`${t('edit')} ${hierarchyLabel.unidade ?? 'Unidade'}`}>
            <span>
              <IconButton
                disabled={onLoading}
                onClick={() => {
                  trackEventMatomo('tela de locais', 'click', 'button', 'abre modal');
                  setOpenEditModal(true);
                }}
                aria-label="edit" color='info'>
                <AiFillTool size={25} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`${t('checklists.checklists.clone')} ${hierarchyLabel.unidade}`}>
            <span>
              <IconButton
                disabled={onLoading}
                onClick={() => clone(unity.id)}
                aria-label="clone" color='success'>
                <AiFillCopy size={25} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`${t('delete')} ${hierarchyLabel.unidade ?? 'Unidade'}`}>
            <span>
              <IconButton
                disabled={onLoading}
                onClick={() => {
                  trackEventMatomo('tela de locais', 'click', 'button', 'remove unidade');
                  setOpenRemove(true);
                }}
                aria-label="delete" color='error'>
                <AiFillDelete size={25} />
              </IconButton>
            </span>
          </Tooltip>
          <QrCodeModal unity={unity} />
        </Stack>
      </Stack>
    </>
  );
}
